import { useState } from 'react';
import { MessageSquareText, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface CannedResponse {
  id: string;
  label: string;
  category: string;
  content: string;
}

const CANNED_RESPONSES: CannedResponse[] = [
  // Greetings
  {
    id: 'greeting-hello',
    label: 'Hello & Thanks',
    category: 'Greetings',
    content: "Hi there! Thanks for reaching out to IT Dukes support. I'm happy to help you with your question.",
  },
  {
    id: 'greeting-followup',
    label: 'Following Up',
    category: 'Greetings',
    content: "Hi! I'm following up on your earlier question. Is there anything else I can help you with?",
  },
  
  // Tool Help
  {
    id: 'tool-spf-dkim',
    label: 'SPF/DKIM Setup Help',
    category: 'Tool Help',
    content: "For SPF/DKIM/DMARC setup, I recommend:\n\n1. First, check your current records using our SPF/DKIM/DMARC Checker tool\n2. Make sure SPF includes all your sending sources\n3. Ensure DKIM is properly signed for your domain\n4. Start with DMARC policy of 'none' and monitor\n\nWould you like me to explain any of these steps in more detail?",
  },
  {
    id: 'tool-deliverability',
    label: 'Deliverability Issues',
    category: 'Tool Help',
    content: "For email deliverability issues, here are some things to check:\n\n• Domain age (new domains often have lower reputation)\n• Authentication records (SPF, DKIM, DMARC)\n• IP/domain blacklist status\n• Email content for spam triggers\n\nOur Email Deliverability Tester can help analyze your specific setup. Have you tried running a test?",
  },
  {
    id: 'tool-blacklist',
    label: 'Blacklist Check',
    category: 'Tool Help',
    content: "If you're experiencing deliverability issues, it's worth checking if your domain or IP is blacklisted. Use our Blacklist Checker tool to scan across multiple blacklist databases.\n\nIf you find yourself listed, most blacklists have a removal request process. Would you like guidance on the delisting process?",
  },
  
  // Account Issues
  {
    id: 'account-reset',
    label: 'Request Limit Reset',
    category: 'Account',
    content: "I understand you've reached your usage limit. You can request a limit reset through the tool interface, or I can help escalate this for you. Could you please confirm your email address so I can look into your account?",
  },
  {
    id: 'account-verification',
    label: 'Verification Help',
    category: 'Account',
    content: "If you're having trouble with email verification:\n\n1. Check your spam/junk folder\n2. Make sure you're using the correct email address\n3. The verification code expires after 10 minutes\n\nWould you like me to help resend the verification email?",
  },
  
  // Closings
  {
    id: 'closing-resolved',
    label: 'Issue Resolved',
    category: 'Closing',
    content: "Great, I'm glad we could resolve this for you! If you have any other questions in the future, don't hesitate to reach out. Have a great day!",
  },
  {
    id: 'closing-followup',
    label: 'Will Follow Up',
    category: 'Closing',
    content: "I'll look into this further and get back to you shortly. In the meantime, feel free to reach out if you have any other questions!",
  },
  {
    id: 'closing-escalate',
    label: 'Escalating Issue',
    category: 'Closing',
    content: "I'm escalating this to our technical team for further investigation. They'll review your case and we'll get back to you within 24-48 hours. Thank you for your patience!",
  },
];

interface CannedResponsesProps {
  onSelect: (content: string) => void;
}

export const CannedResponses = ({ onSelect }: CannedResponsesProps) => {
  const categories = [...new Set(CANNED_RESPONSES.map(r => r.category))];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 gap-1.5">
          <MessageSquareText className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">Templates</span>
          <ChevronDown className="w-3 h-3" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64 max-h-80 overflow-y-auto">
        {categories.map((category, idx) => (
          <div key={category}>
            {idx > 0 && <DropdownMenuSeparator />}
            <DropdownMenuLabel className="text-xs text-muted-foreground">
              {category}
            </DropdownMenuLabel>
            {CANNED_RESPONSES.filter(r => r.category === category).map((response) => (
              <DropdownMenuItem
                key={response.id}
                onClick={() => onSelect(response.content)}
                className="cursor-pointer"
              >
                <span className="text-sm">{response.label}</span>
              </DropdownMenuItem>
            ))}
          </div>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
